import { __rest } from 'tslib';
import { jsxs, jsx } from 'react/jsx-runtime';
import MuiBox from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import MuiTypography from '@mui/material/Typography';
import { useRef, useState, useEffect } from 'react';
import BaseTimeInput from '../BaseTimeInput/BaseTimeInput.js';
import TimeInputContainer from '../BaseTimeInput/components/TimeInputContainer.js';
import useTimeInput from '../BaseTimeInput/hooks/useTimeInput.js';
import { getLimits } from '../../utils/timeUtils.js';

const Root = styled(TimeInputContainer) `
  ${({ theme, error, modifiedManually, disabled, inactive, isInitialState }) => `
    ${isInitialState && inactive && !disabled && `color: ${theme.palette.N30.main}`};
    #prefix {
      color: ${error || modifiedManually ? 'currentColor' : theme.palette[inactive ? 'N40' : 'N30'].main};
    }
    &:focus-within #prefix {
      color: ${theme.palette.N30.main};
    }
    &:hover #prefix {
      color: ${!disabled && !error && theme.palette.N30.main};
    }
    ${disabled &&
    `
      cursor: not-allowed !important;
      #prefix {
        cursor: not-allowed;
        color: ${theme.palette.N40.main};
      }
    `}
  `}
`;
const _TimeInput = styled(BaseTimeInput) `
  ${({ theme }) => `
    padding: ${theme.spacing(0, 0.5, 0, 0)};
    margin: ${theme.spacing(0, 0, 0.25, 0)};
  `}
`;
const _HoursInput = styled(_TimeInput) `
  ${({ width }) => `
    .MuiInputBase-input {
      direction: rtl;
      width: ${width}px;  
    }  
    `}
  &:before {
    left: unset;
    right: -5px;
  }
`;
const MockInput = styled(MuiBox) `
  position: absolute;
  top: 0;
  left: 0;
  z-index: 999;
  visibility: hidden;
  ${({ theme, size }) => `
    padding: ${size === 'small' ? theme.spacing(0.125, 1.5, 0.125, 0) : theme.spacing(0.75, 1.5, 0.75, 0)};
  `};
`;
const InputWrapper = styled(MuiBox) `
  display: flex;
  justify-content: flex-start;
  height: 100%;
  align-items: center;
  ${({ theme, size }) => `
    margin: ${theme.spacing(0.25, 0, 0, 0)};
    padding: ${size === 'small' ? theme.spacing(0.125, 1, 0.125, 0) : theme.spacing(0.75, 1, 0.75, 0)};
    :not(:last-of-type) {
      flex: 1;
      padding-right: ${theme.spacing(1.5)};
    }

    :last-of-type {
      flex-grow: 1;
    }
  `};
`;
const PrefixLabel = styled(MuiTypography) `
  transition: color ${({ theme }) => theme.transitions.duration.fast} linear,
    background-color ${({ theme }) => theme.transitions.duration.fast} linear,
    border ${({ theme }) => theme.transitions.duration.fast} linear;
`;
const TimeInput = (_a) => {
    var { className, size, onValueChange, error, disabled, defaultValue, labels, value, hourLimit = 99, 'data-testid': testId } = _a, rest = __rest(_a, ["className", "size", "onValueChange", "error", "disabled", "defaultValue", "labels", "value", "hourLimit", 'data-testid']);
    const limits = getLimits(hourLimit);
    const data = useTimeInput({ limits, onValueChange, defaultValue, value });
    const hoursInputRef = useRef(null);
    const mockRef = useRef(null);
    const [dynamicWidth, setDynamicWidth] = useState();
    const hasError = data.error || error;
    useEffect(() => {
        if (mockRef.current) {
            setDynamicWidth(mockRef.current.clientWidth);
        }
    }, [hourLimit]);
    return (jsxs(Root, Object.assign({ className: className, disabled: disabled, isInitialState: data.isInitialState, size: size, error: hasError, "data-testid": testId }, rest, { children: [jsxs(InputWrapper, Object.assign({ onClick: () => hoursInputRef.current && hoursInputRef.current.focus() }, { children: [jsx(MockInput, Object.assign({ size: size, ref: mockRef }, { children: limits.hours.max })), jsx(_HoursInput, { disabled: disabled, type: "number", inputProps: limits.hours, inputRef: hoursInputRef, value: data.hours, error: hasError, hint: labels === null || labels === void 0 ? void 0 : labels.hoursHint, onValueChange: data.setHours, width: dynamicWidth, "data-testid": testId ? `${testId}-hours` : undefined }), jsx(PrefixLabel, Object.assign({ id: "prefix", variant: "body1" }, { children: (labels === null || labels === void 0 ? void 0 : labels.hoursLabel) || 'h' }))] })), jsxs(InputWrapper, Object.assign({ onClick: () => data.minutesInputRef.current && data.minutesInputRef.current.focus() }, { children: [jsx(_TimeInput, { disabled: disabled, type: "number", inputProps: limits.minutes, inputRef: data.minutesInputRef, value: data.minutes, error: hasError, hint: labels === null || labels === void 0 ? void 0 : labels.minutesHint, onValueChange: data.setMinutes, "data-testid": testId ? `${testId}-minutes` : undefined }), jsx(PrefixLabel, Object.assign({ id: "prefix", variant: "body1" }, { children: (labels === null || labels === void 0 ? void 0 : labels.minutesLabel) || 'm' }))] }))] })));
};

export { TimeInput as default };
//# sourceMappingURL=TimeInput.js.map
